<template>
  <div>
    <v-progress-linear v-if="loading" :indeterminate="true"/>
    <v-container v-if="!loading" class="customer-analytics" fluid>
      <v-row>
        <v-col>
          <h1>Customer Analytics</h1>
          <p class="text--secondary">Customer signups breakdown by week and year</p>
          <v-expansion-panels flat hover>
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0 text--secondary" style="min-height: 32px;">
                <span>
                  <v-icon class="mr-1" small>mdi-information-outline</v-icon>
                  Which customers are counted?
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="text-body-2">
                <p class="mb-2">
                  <strong>Included:</strong> every customer account with a signup date — the date the
                  account was registered, or for older accounts the date their Stripe customer record
                  was created. Deleted accounts still count as signups (they did sign up) and are
                  flagged as churned; customers who never purchased are also counted, with the
                  Purchased column showing how many actually ordered.
                </p>
                <p class="mb-2">
                  <strong>Excluded:</strong> subaccounts (accounts attached to a main account) appear
                  nowhere on this page — they don't pay for their own meals, so counting them as
                  signups or sales would double-count the household. Accounts with no signup date at
                  all (no registration date and no Stripe record) are also excluded.
                </p>
                <p class="mb-0">
                  A customer counts as <strong>churned</strong> when their account is deleted or their
                  last Stripe charge is older than the churn range set above. The stream, region, and
                  date range filters apply to every table and chart on the page.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex justify-center align-center" cols="12">
          <v-select
            v-model="selectedStream"
            :items="streamOptions"
            class="mr-2"
            clearable
            dense
            hide-details
            label="Filter by Stream (user's current plan)"
            outlined
            @change="onStreamFilterChange"

          >
            <template v-slot:prepend-inner>
              <v-icon>mdi-filter</v-icon>
            </template>
          </v-select>
          <v-select
            v-model="selectedRegion"
            :items="regionOptions"
            class="mr-2"
            clearable
            dense
            hide-details
            label="Filter by Region (postcode delivery area)"
            outlined
          >
            <template v-slot:prepend-inner>
              <v-icon>mdi-map-marker</v-icon>
            </template>
          </v-select>
          <v-text-field
            v-model.number="churnWeeks"
            class="mr-2"
            dense
            hide-details
            label="Churn Range (weeks)"
            max="52"
            min="1"
            outlined
            style="width: 180px;"
            type="number"
          />
          <v-select
            v-model="weekCutoffDay"
            :items="weekDayOptions"
            class="mr-2"
            dense
            hide-details
            label="Week starts"
            outlined
            style="width: 160px;"
          />
          <v-text-field
            v-model.number="weekCutoffHour"
            class="mr-2"
            dense
            hide-details
            label="at hour (0–23)"
            max="23"
            min="0"
            outlined
            style="width: 130px;"
            type="number"
          />
          <v-btn
            class="mr-2"
            outlined
            @click="openDateRangeDialog"
          >
            <v-icon left>mdi-calendar-range</v-icon>
            {{ dateRangeButtonText }}
          </v-btn>

        </v-col>
        <v-col class="d-flex align-center" cols="12" md="9">
          <v-chip
            v-if="selectedStream"
            class="mr-2"
            color="primary"
            small
          >
            <v-icon left small>mdi-filter</v-icon>
            Stream: {{ selectedStream }}
          </v-chip>
          <v-chip
            v-if="selectedRegion"
            class="mr-2"
            color="primary"
            small
          >
            <v-icon left small>mdi-map-marker</v-icon>
            Region: {{ selectedRegionLabel }}
          </v-chip>
          <span v-if="selectedStream || selectedRegion" class="text--secondary caption">
            Showing {{ filteredCustomers.length }} customers ({{ customers.length }} total)
          </span>
        </v-col>
      </v-row>

      <v-tabs v-model="activeTab" centered>
        <v-tab>Weekly View</v-tab>
        <v-tab>Year Comparison</v-tab>
        <v-tab>UTM Campaign Breakdown</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Weekly View Tab -->
        <v-tab-item>


          <v-tabs v-model="weeklyTab" centered class="mt-4">
            <v-tab>Signups</v-tab>
            <v-tab v-if="finance">Sales</v-tab>
          </v-tabs>

          <v-tabs-items v-model="weeklyTab">
            <!-- Signups Sub-tab -->
            <v-tab-item>
              <v-row class="mt-4">
                <v-col cols="12" lg="9">
                  <v-card>
                    <v-card-title>
                      <span class="text-h6">Weekly Customer Signups</span>
                      <v-spacer/>
                      <v-btn class="mr-2" outlined small @click="downloadWeeklyCSV">
                        <v-icon left>mdi-download</v-icon>
                        Download CSV
                      </v-btn>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn :loading="refreshing" outlined small v-bind="attrs" v-on="on" @click="refreshData()">
                            <v-icon left>mdi-refresh</v-icon>
                            Refresh
                          </v-btn>
                        </template>
                        <span v-if="refreshing">
                          Refreshing Stripe stats…
                          {{ refreshProgress ? `${refreshProgress.processed}/${refreshProgress.affected}` : '' }}
                        </span>
                        <span v-else>
                          <span v-if="cachedAt">Cached {{ new Date(cachedAt).toLocaleString() }}</span>
                          <span v-else>No cache</span>
                          <span v-if="statsCurrentTo"> — sales data through {{ statsCurrentTo }}</span>
                        </span>
                      </v-tooltip>
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn :disabled="refreshing" icon small v-bind="attrs" v-on="on">
                            <v-icon>mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="completeRefresh">
                            <v-list-item-icon class="mr-2">
                              <v-icon small>mdi-refresh-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Complete refresh</v-list-item-title>
                              <v-list-item-subtitle>Re-pulls every customer from Stripe — very slow</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-card-title>
                    <v-card-text>
                      <v-data-table
                        :headers="signupHeaders"
                        :items="weeklySignups"
                        :items-per-page="-1"
                        :loading="loading"
                        dense
                        hide-default-footer
                        sort-by="weekStart"
                        sort-desc
                      >
                        <template v-slot:item.actions="{ item }">
                          <v-btn icon x-small @click="weekDetailWeekStart = item.weekStart; weekDetailDialog = true">
                            <v-icon small>mdi-account-group</v-icon>
                          </v-btn>
                        </template>
                        <template v-slot:item.weekStart="{ item }">
                          {{ formatWeekRange(item.weekStart) }}
                        </template>
                        <template v-slot:item.vsMovingAverage="{ item }">
                          <div v-if="item.vsMovingAverage" class="d-flex align-center">
                            <v-chip
                              :color="getMovingAverageColor(item.vsMovingAverage.percentage)"
                              :text-color="getMovingAverageTextColor(item.vsMovingAverage.percentage)"
                              small
                            >
                              {{ item.vsMovingAverage.percentage > 0 ? '+' : '' }}{{ item.vsMovingAverage.percentage }}%
                            </v-chip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  class="ml-1"
                                  color="grey"
                                  small
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  mdi-information
                                </v-icon>
                              </template>
                              <span>2-month average: {{ item.vsMovingAverage.movingAverage }}</span>
                            </v-tooltip>
                          </div>
                          <span v-else class="text--disabled">N/A</span>
                        </template>
                        <template v-slot:item.purchasedCount="{ item }">
                          <span>{{
                              item.purchasedCount
                            }} ({{ ((item.purchasedCount / item.signupCount) * 100).toFixed() }}%)</span>
                        </template>
                        <template v-slot:item.churnedCustomers="{ item }">
                          <span>{{
                              item.churnedCustomers
                            }} ({{ ((item.churnedCustomers / item.signupCount) * 100).toFixed() }}%)</span>
                        </template>
                        <template v-slot:item.utmCampaignCount="{ item }">
                          <span>{{ item.utmCampaignCount }}</span>
                        </template>
                        <template v-slot:item.utmPercentage="{ item }">
                          <v-chip
                            :color="getUTMPercentageColor(item.utmPercentage)"
                            :text-color="getUTMPercentageTextColor(item.utmPercentage)"
                            small
                          >
                            {{ item.utmPercentage }}%
                          </v-chip>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" lg="3">
                  <v-card>
                    <v-card-title>Summary Statistics</v-card-title>
                    <v-card-text>
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Total Customers</v-list-item-title>
                            <v-list-item-subtitle>{{ totalCustomers }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Total Weeks</v-list-item-title>
                            <v-list-item-subtitle>{{ weeklySignups.length }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Average Signups per Week</v-list-item-title>
                            <v-list-item-subtitle>{{ avgSignupsPerWeek }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Average Purchased per Week</v-list-item-title>
                            <v-list-item-subtitle>{{ avgPurchasedPerWeek }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Average Churned per Week</v-list-item-title>
                            <v-list-item-subtitle>{{ avgChurnedPerWeek }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Peak Week</v-list-item-title>
                            <v-list-item-subtitle>{{ peakWeek }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Sales Sub-tab -->
            <v-tab-item>
              <v-row class="mt-4">
                <v-col cols="12" lg="9">
                  <v-card>
                    <v-card-title>
                      <span class="text-h6">Weekly Sales Analytics</span>
                      <v-spacer/>
                      <v-btn class="mr-2" outlined small @click="downloadSalesCSV">
                        <v-icon left>mdi-download</v-icon>
                        Download CSV
                      </v-btn>
                      <v-btn :loading="refreshing" outlined small @click="refreshData()">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-data-table
                        :headers="salesHeaders"
                        :items="weeklySalesData"
                        :items-per-page="-1"
                        :loading="loading"
                        dense
                        hide-default-footer
                        sort-by="weekStart"
                        sort-desc
                      >
                        <template v-slot:item.weekStart="{ item }">
                          {{ formatWeekRange(item.weekStart) }}
                        </template>
                        <template v-slot:item.totalSales="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.totalSales) }}
                          </span>
                        </template>
                        <template v-slot:item.totalInvoices="{ item }">
                          <span>{{ item.totalInvoices }}</span>
                        </template>
                        <template v-slot:item.avgSalesPerInvoice="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.avgSalesPerInvoice) }}
                          </span>
                        </template>
                        <template v-slot:item.avgNetPerCustomer="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.avgNetPerCustomer) }}
                          </span>
                        </template>
                        <template v-slot:item.medianSales="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.medianSales) }}
                          </span>
                        </template>
                        <template v-slot:item.medianInvoices="{ item }">
                          <span>{{ item.medianInvoices }}</span>
                        </template>
                        <template v-slot:item.medianSalesPerInvoice="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.medianSalesPerInvoice) }}
                          </span>
                        </template>
                        <template v-slot:item.medianNetPerCustomer="{ item }">
                          <span class="font-weight-medium">
                            {{ formatCurrency(item.medianNetPerCustomer) }}
                          </span>
                        </template>
                        <template v-slot:item.churnInfo="{ item }">
                          <div class="d-flex flex-column">
                            <v-progress-linear
                              :color="getChurnColor(item.churnInfo.count)"
                              :text-color="getChurnTextColor(item.churnInfo.count)"
                              :value="item.churnInfo.percentage"


                            >

                            </v-progress-linear>
                            <div class="text-caption">
                              {{ item.churnInfo.percentage }}% ({{ item.churnInfo.count }} of
                              {{ item.churnInfo.totalCustomers }} customers)
                            </div>
                          </div>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" lg="3">
                  <v-card>
                    <v-card-title>Sales Summary</v-card-title>
                    <v-card-text>
                      <!-- Totals Section -->
                      <div class="mb-4">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title>Total Sales</v-list-item-title>
                              <v-list-item-subtitle>{{ formatCurrency(totalSalesAmount) }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title>Total Invoices</v-list-item-title>
                              <v-list-item-subtitle>{{ totalInvoicesAmount }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title>Total Churned</v-list-item-title>
                              <v-list-item-subtitle>{{ ((totalChurnedCustomers / totalCustomers) * 100).toFixed() }}%
                                ({{ totalChurnedCustomers }} of {{ totalCustomers }})
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </div>

                      <!-- Average vs Median Comparison Table -->
                      <v-simple-table dense>
                        <thead>
                        <tr>
                          <th class="text-left">Metric</th>
                          <th class="text-right">Average</th>
                          <th class="text-right">Median</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td class="font-weight-medium">Sales</td>
                          <td class="text-right">{{ formatCurrency(totalSalesAmount / weeklySalesData.length) }}</td>
                          <td class="text-right">{{ formatCurrency(medianSalesTotal) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Invoices</td>
                          <td class="text-right">{{
                              Math.round((totalInvoicesAmount / weeklySalesData.length) * 10) / 10
                            }}
                          </td>
                          <td class="text-right">{{ medianInvoicesTotal }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Invoice $</td>
                          <td class="text-right">{{ formatCurrency(avgSalesPerInvoiceTotal) }}</td>
                          <td class="text-right">{{ formatCurrency(medianSalesPerInvoiceTotal) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Customer $</td>
                          <td class="text-right">{{ formatCurrency(avgSalesPerCustomer) }}</td>
                          <td class="text-right">{{ formatCurrency(medianNetPerCustomerTotal) }}</td>
                        </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                  <!-- Invoice Distribution Chart -->
                  <v-card class="mt-4">
                    <v-card-text>
                      <v-checkbox
                        v-model="showWithClipping"
                        dense
                        hide-details
                        label="Clip Outliers in Charts"
                      />
                      <div v-if="!showWithClipping" class="text-caption text--secondary mb-3">
                        Note: Values are clipped by bounds (outliers removed using IQR method)
                      </div>
                    </v-card-text>
                  </v-card>
                  <v-card class="mt-4">
                    <!--                    <v-card-title>-->
                    <!--                      <span class="text-h6">Invoice Distribution</span>-->
                    <!--                    </v-card-title>-->
                    <v-card-text>
                      <v-row>
                        <v-col cols="6">
                          <InvoiceDistributionChart :customers="filteredCustomers" :showWithClipping="showWithClipping"
                                                    style="height: 400px;"/>
                        </v-col>
                        <v-col cols="6">
                          <NetDistributionChart :customers="filteredCustomers" :showWithClipping="showWithClipping"
                                                style="height: 400px;"/>
                        </v-col>
                      </v-row>
                    </v-card-text>

                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>

        <!-- Year Comparison Tab -->
        <v-tab-item>
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  <span class="text-h6">Weekly Signups by Year Comparison</span>
                  <v-spacer/>
                  <v-btn class="mr-2" outlined small @click="downloadYearlyCSV">
                    <v-icon left>mdi-download</v-icon>
                    Download CSV
                  </v-btn>
                  <v-btn :loading="refreshing" outlined small @click="refreshData()">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="yearlyHeaders"
                    :items="yearlyComparison"
                    :items-per-page="-1"
                    :loading="loading"
                    dense
                    hide-default-footer
                    sort-by="weekNumber"
                  >
                    <template v-slot:item.weekRange="{ item }">
                      <span class="text-body-2">{{ item.weekRange }}</span>
                    </template>
                    <!-- slot templates define one named slot per year, not a rendered list -
                         the slot name is already the key, so the div needs none. Vue 2 rejects
                         a key on the <template> itself ("<template> cannot be keyed"). -->
                    <template v-for="year in availableYears" v-slot:[`item.year_${year}`]="{ item }">
                      <!-- eslint-disable-next-line vue/require-v-for-key -->
                      <div class="d-flex align-center">
                        <span v-if="item[`year_${year}`] > 0" :class="getChangeClass(item[`change_${year}`])">
                          {{ item[`year_${year}`] }}
                        </span>
                        <v-chip
                          v-if="item[`year_${year}`] > 0 && item[`change_${year}`] !== null && item[`change_${year}`] !== 0"
                          :color="item[`change_${year}`] > 0 ? 'success' : 'error'"
                          class="ml-1"
                          x-small
                        >
                          {{ item[`change_${year}`] > 0 ? '+' : '' }}{{ item[`change_${year}`] }}
                        </v-chip>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-title>Yearly Summary</v-card-title>
                <v-card-text>
                  <v-simple-table>
                    <thead>
                    <tr>
                      <th>Year</th>
                      <th>Total Signups</th>
                      <th>Average per Week</th>
                      <th>Peak Week</th>
                      <th>vs Previous Year</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="summary in yearlySummary" :key="summary.year">
                      <td><strong>{{ summary.year }}</strong></td>
                      <td>{{ summary.totalSignups }}</td>
                      <td>{{ summary.avgPerWeek }}</td>
                      <td>{{ summary.peakWeek }}</td>
                      <td>
                        <v-chip
                          v-if="summary.vsLastYear !== null"
                          :color="summary.vsLastYear > 0 ? 'success' : 'error'"
                          small
                        >
                          {{ summary.vsLastYear > 0 ? '+' : '' }}{{ summary.vsLastYear }}%
                        </v-chip>
                        <span v-else class="text--disabled">N/A</span>
                      </td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- UTM Campaign Breakdown Tab -->
        <v-tab-item>
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  <span class="text-h6">Weekly Signups by UTM Campaign</span>
                  <v-spacer/>
                  <v-btn class="mr-2" outlined small @click="downloadUTMCSV">
                    <v-icon left>mdi-download</v-icon>
                    Download CSV
                  </v-btn>
                  <v-btn :loading="refreshing" outlined small @click="refreshData()">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <div v-for="month in utmMonthlyTables" :key="month.monthKey" class="mb-6">
                    <div class="text-subtitle-1 font-weight-medium mb-2">{{ month.title }}</div>
                    <v-data-table
                      :headers="month.headers"
                      :items="month.items"
                      :items-per-page="-1"
                      :loading="loading"
                      dense
                      hide-default-footer
                      sort-by="weekStart"
                      sort-desc
                    >
                      <template v-slot:item.weekStart="{ item }">
                        {{ formatWeekRange(item.weekStart) }}
                      </template>
                    </v-data-table>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-title>UTM Campaign Summary</v-card-title>
                <v-card-text>
                  <v-simple-table>
                    <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Total Signups</th>
                      <th>Average per Week</th>
                      <th>Peak Week</th>
                      <th>% of Total</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="summary in utmSummary" :key="summary.campaign">
                      <td><strong>{{ summary.campaign }}</strong></td>
                      <td>{{ summary.totalSignups }}</td>
                      <td>{{ summary.avgPerWeek }}</td>
                      <td>{{ summary.peakWeek }}</td>
                      <td>
                        <v-chip
                          :color="getPercentageColor(summary.percentage)"
                          small
                        >
                          {{ summary.percentage }}%
                        </v-chip>
                      </td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>

      <v-row v-if="error">
        <v-col>
          <v-alert outlined type="error">
            {{ error }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Signup Detail Dialog -->
      <v-dialog v-model="weekDetailDialog" max-width="900px">
        <v-card>
          <v-card-title>
            <span class="text-h6">Signups — {{ weekDetailWeekStart ? formatWeekRange(weekDetailWeekStart) : '' }}</span>
            <v-spacer/>
            <v-btn icon @click="weekDetailDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text style="max-height: 70vh; overflow-y: auto;">
            <v-chip-group v-model="weekDetailFilter" active-class="primary--text" column>
              <v-chip filter outlined small value="ordered">Ordered ({{ weekDetailCounts.ordered }})</v-chip>
              <v-chip filter outlined small value="not-ordered">Not ordered ({{ weekDetailCounts.notOrdered }})</v-chip>
              <v-chip filter outlined small value="deleted">Deleted ({{ weekDetailCounts.deleted }})</v-chip>
            </v-chip-group>
            <v-list dense>
              <v-list-item v-for="item in weekDetailCustomers" :key="item.email">
                <v-list-item-icon class="mr-2 align-self-center">
                  <v-icon :color="item.purchased ? 'success' : 'grey'" small>
                    {{ item.purchased ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    <CustomerHeader :customer="item.customer"/>
                    <v-chip v-if="item.isDeleted" class="ml-2" color="error" outlined x-small>deleted</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>Signed up {{ item.signupDate }}</v-list-item-subtitle>
                  <v-list-item-subtitle>
                    {{ formatCurrency(item.net) }} net
                    · {{ item.invoiceCount }} {{ item.invoiceCount === 1 ? 'invoice' : 'invoices' }}
                    <span v-if="item.lastCharge"> · last charge {{ item.lastCharge }}</span>
                    <span v-if="item.planName"> · {{ item.planName }}</span>
                    <span v-if="item.utmCampaign"> · utm: {{ item.utmCampaign }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn :href="`#/customers/${item.uid}/stripe`" icon small target="_blank" title="Open Stripe page">
                    <v-icon small>mdi-credit-card-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Refresh Progress / Result Dialog -->
      <v-dialog v-model="refreshDialog" max-width="480px">
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ refreshing ? 'Refreshing customer data…' : 'Refresh result' }}</span>
            <v-spacer/>
            <v-btn icon @click="refreshDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <template v-if="refreshing">
              <v-progress-linear class="mb-3" indeterminate/>
              <v-simple-table v-if="refreshStatus && refreshStatus.progress" dense>
                <tbody>
                <tr v-if="refreshStatus.progress.phase">
                  <td>Phase</td>
                  <td>{{ refreshStatus.progress.phase }}</td>
                </tr>
                <tr>
                  <td>Customers affected</td>
                  <td>{{ refreshStatus.progress.affected }}</td>
                </tr>
                <tr>
                  <td>Processed so far</td>
                  <td>{{ refreshStatus.progress.processed }}</td>
                </tr>
                <tr>
                  <td>Updated</td>
                  <td>{{ refreshStatus.progress.refreshed }}</td>
                </tr>
                <tr>
                  <td>Unchanged</td>
                  <td>{{ refreshStatus.progress.unchanged }}</td>
                </tr>
                <tr>
                  <td>Failed</td>
                  <td>{{ refreshStatus.progress.failed }}</td>
                </tr>
                <tr v-if="refreshStatus.progress.mixpanelFetched !== undefined">
                  <td>Mixpanel profiles fetched</td>
                  <td>{{ refreshStatus.progress.mixpanelFetched }}</td>
                </tr>
                <tr v-if="refreshStatus.progress.mixpanelUpdated !== undefined">
                  <td>Mixpanel customers updated</td>
                  <td>{{ refreshStatus.progress.mixpanelUpdated }}</td>
                </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="text--secondary">Starting…</div>
            </template>
            <template v-else-if="refreshLastResult">
              <v-simple-table dense>
                <tbody>
                <tr>
                  <td>Mode</td>
                  <td>{{ refreshLastResult.mode }}<span v-if="refreshLastResult.dryRun"> (dry run)</span></td>
                </tr>
                <tr>
                  <td>Customers affected</td>
                  <td>{{ refreshLastResult.affectedCount }}</td>
                </tr>
                <tr>
                  <td>Updated</td>
                  <td>{{ refreshLastResult.refreshedCount }}</td>
                </tr>
                <tr>
                  <td>Unchanged</td>
                  <td>{{ refreshLastResult.unchangedCount }}</td>
                </tr>
                <tr>
                  <td>Unmatched Stripe customers</td>
                  <td>{{ refreshLastResult.unmatched }}</td>
                </tr>
                <tr>
                  <td>Failed</td>
                  <td>{{ refreshLastResult.failedCount }}</td>
                </tr>
                <tr>
                  <td>Duration</td>
                  <td>{{ Math.round(refreshLastResult.durationMs / 100) / 10 }}s</td>
                </tr>
                </tbody>
              </v-simple-table>
              <div v-if="refreshLastResult.failedCount" class="mt-2 text-caption">
                Failed uids (sample): {{ refreshLastResult.failedUids.join(', ') }}
              </div>
              <template v-if="refreshLastResult.mixpanel">
                <div class="text-subtitle-2 mt-4">Mixpanel (UTM) sync</div>
                <v-alert
                  v-if="refreshLastResult.mixpanel.error"
                  class="mt-2 mb-0"
                  dense
                  outlined
                  type="warning"
                >
                  {{ refreshLastResult.mixpanel.error }}
                </v-alert>
                <v-simple-table v-else dense>
                  <tbody>
                  <tr>
                    <td>Profiles fetched</td>
                    <td>{{ refreshLastResult.mixpanel.fetched }}</td>
                  </tr>
                  <tr>
                    <td>Matched to customers</td>
                    <td>{{ refreshLastResult.mixpanel.matched }}</td>
                  </tr>
                  <tr>
                    <td>Updated</td>
                    <td>{{ refreshLastResult.mixpanel.updated }}</td>
                  </tr>
                  <tr>
                    <td>Unchanged</td>
                    <td>{{ refreshLastResult.mixpanel.unchanged }}</td>
                  </tr>
                  <tr>
                    <td>Unmatched profiles</td>
                    <td>{{ refreshLastResult.mixpanel.unmatched }}</td>
                  </tr>
                  </tbody>
                </v-simple-table>
              </template>
            </template>
            <v-alert
              v-else-if="refreshStatus && refreshStatus.lastError"
              class="mb-0"
              dense
              outlined
              type="error"
            >
              {{ refreshStatus.lastError }}
            </v-alert>
          </v-card-text>
          <v-card-actions v-if="!refreshing">
            <v-spacer/>
            <v-btn text @click="refreshDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dateRangeDialog" max-width="400px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h6">Date Range Filter</span>
            <v-spacer/>
            <v-btn icon @click="dateRangeDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="tempStartingWeeks"
                    dense
                    hint="How many weeks back from today to start"
                    label="Starting Week (weeks from today)"
                    max="208"
                    min="0"
                    outlined
                    persistent-hint
                    type="number"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="tempRangeDuration"
                    dense
                    hint="How many weeks after the starting week to include"
                    label="Range Duration (weeks)"
                    max="208"
                    min="1"
                    outlined
                    persistent-hint
                    type="number"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="clearDateRange">Clear</v-btn>
            <v-spacer/>
            <v-btn text @click="dateRangeDialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="applyDateRange">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import api from '@/api'
import {csvAsTable, csvExport} from '@/store/utils'
import {mapActions} from 'vuex'
import {formatCurrency} from "../store/utils";
import urlState from "../router/urlState";
import InvoiceDistributionChart from '@/components/charts/InvoiceDistributionChart.vue'
import NetDistributionChart from '@/components/charts/NetDistributionChart.vue'
import CustomerHeader from "@/components/CustomerHeader";

// Only the customer attributes this analytics view actually reads. Requested as
// a projection on GET v2/customers so we don't download the full customer object
// (schedule, subAccounts, ingredient_preferences, etc.) for the whole
// directory. Sub-objects stripe/stats/mixpanel/address are returned whole by the
// top-level projection; only their .created_at / .net,invoiceCount,lastCharge /
// .initial_utm_campaign / .postal_code are used. mainAccount is fetched only to
// exclude subaccounts - they don't pay for their own meals, so counting them as
// signups/sales would be wrong.
const ANALYTICS_CUSTOMER_FIELDS = [
  'uid', 'email', 'first_name', 'last_name', 'plan', 'isDeleted',
  'account_created', 'stripe', 'stats', 'mixpanel', 'mainAccount', 'address',
];

// Customers whose postal code matches no delivery region (or who have no
// address at all) are grouped under this pseudo-region so they stay filterable.
const NO_REGION = '(no region)';

export default {
  name: 'CustomerAnalytics',
  components: {
    InvoiceDistributionChart,
    NetDistributionChart,
    CustomerHeader
  },
  props: {
    finance: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,
      cachedAt: null,
      refreshing: false,
      refreshProgress: null,
      refreshDialog: false,
      refreshStatus: null,
      customers: [],
      plans: {},
      postcodeRegions: {},
      activeTab: 0,
      weeklyTab: 0,
      selectedStream: null,
      selectedRegion: null,
      churnWeeks: 12,
      dateRangeDialog: false,
      startingWeeks: null,
      rangeDuration: null,
      tempStartingWeeks: null,
      tempRangeDuration: null,
      showWithClipping: true,
      weekDetailDialog: false,
      weekDetailWeekStart: null,
      weekDetailFilter: null,
      weekCutoffDay: 3,
      weekCutoffHour: 12,
      weekDayOptions: [
        {text: 'Sunday', value: 0},
        {text: 'Monday', value: 1},
        {text: 'Tuesday', value: 2},
        {text: 'Wednesday', value: 3},
        {text: 'Thursday', value: 4},
        {text: 'Friday', value: 5},
        {text: 'Saturday', value: 6},
      ],
      signupHeaders: [
        {text: '', value: 'actions', width: '40px', sortable: false},
        {text: 'Week Starting', value: 'weekStart', width: '200px'},
        {text: 'Signups', value: 'signupCount', width: '150px'},
        {text: 'Purchased', value: 'purchasedCount', width: '150px'},
        {text: 'Churned', value: 'churnedCustomers', width: '150px'},
        {text: 'vs 2-Month Avg', value: 'vsMovingAverage', width: '150px'},
        {text: 'UTM Campaign', value: 'utmCampaignCount', width: '150px'},
        {text: 'UTM %', value: 'utmPercentage', width: '100px'},
      ],
      salesHeaders: [
        {text: 'Week Starting', value: 'weekStart', width: '200px'},
        {text: 'Total Sales ($)', value: 'totalSales', width: '150px'},
        // {text: 'Median Sales ($)', value: 'medianSales', width: '150px'},
        {text: 'Total Invoices', value: 'totalInvoices', width: '120px'},
        // {text: 'Median Invoices', value: 'medianInvoices', width: '120px'},
        {text: 'Avg Sales/Invoice', value: 'avgSalesPerInvoice', width: '120px'},
        {text: 'Median Sales/Invoice', value: 'medianSalesPerInvoice', width: '120px'},
        {text: 'Avg Net/Customer', value: 'avgNetPerCustomer', width: '120px'},
        {text: 'Median Net/Customer', value: 'medianNetPerCustomer', width: '120px'},
        {text: 'Churn Info', value: 'churnInfo', width: '180px'},
      ]
    }
  },
  computed: {
    refreshLastResult() {
      // hide the previous run's result while a new one is in flight, and any
      // stale result when the run errored out
      if (this.refreshing || !this.refreshStatus || this.refreshStatus.lastError) return null
      return this.refreshStatus.lastResult || null
    },
    // Date of the newest Stripe charge reflected in any customer's stats -
    // i.e. how current the sales numbers are.
    statsCurrentTo() {
      const newestCharge = this.customers.reduce(
        (newest, customer) => Math.max(newest, (customer.stats && customer.stats.last) || 0), 0)
      return newestCharge ? moment.unix(newestCharge).format('MMM D, YYYY') : null
    },
    weekDetailAllCustomers() {
      if (!this.weekDetailWeekStart) return []
      return this.filteredCustomers
        .filter(customer => {
          if (!customer._signup) return false
          const signupDate = moment(customer._signup)
          return signupDate.isValid() && this.getWeekStartForSignup(signupDate) === this.weekDetailWeekStart
        })
        .map(customer => {
          const stats = customer.stats || {}
          const plan = customer.plan && this.plans[customer.plan]
          return {
            email: customer.email,
            uid: customer.uid,
            purchased: stats.invoiceCount > 0,
            hasOrdered: (stats.net || 0) > 0,
            isDeleted: !!customer.isDeleted,
            net: stats.net || 0,
            invoiceCount: stats.invoiceCount || 0,
            lastCharge: stats.lastCharge,
            planName: plan ? (plan.name || customer.plan) : customer.plan,
            utmCampaign: customer.mixpanel && customer.mixpanel.initial_utm_campaign,
            signupDate: moment(customer._signup).format('MMM D, YYYY h:mm a'),
            customer,
          }
        })
        .sort((a, b) => a.email.localeCompare(b.email))
    },
    weekDetailCounts() {
      const all = this.weekDetailAllCustomers
      return {
        ordered: all.filter(c => c.hasOrdered).length,
        notOrdered: all.filter(c => !c.hasOrdered).length,
        deleted: all.filter(c => c.isDeleted).length,
      }
    },
    weekDetailCustomers() {
      const all = this.weekDetailAllCustomers
      if (this.weekDetailFilter === 'ordered') return all.filter(c => c.hasOrdered)
      if (this.weekDetailFilter === 'not-ordered') return all.filter(c => !c.hasOrdered)
      if (this.weekDetailFilter === 'deleted') return all.filter(c => c.isDeleted)
      return all
    },
    streamOptions() {
      if (!this.customers || this.customers.length === 0 || !this.plans) return []

      const streams = new Set()
      this.customers.forEach(customer => {
        if (customer.plan && this.plans[customer.plan]) {
          const plan = this.plans[customer.plan]
          if (plan.stream) {
            streams.add(plan.stream)
          }
        }
      })

      return Array.from(streams).sort()
    },
    regionOptions() {
      if (!this.customers || this.customers.length === 0) return []

      const customerCounts = {}
      this.customers.forEach(customer => {
        if (customer._region) {
          customerCounts[customer._region] = (customerCounts[customer._region] || 0) + 1
        }
      })

      // 'local:Calgary:AB' reads as 'Calgary' - the city is what people ask for.
      // Two regions can share a city name (a different area or province), and
      // those keep the full region id so they stay distinguishable.
      const regions = Object.keys(customerCounts)
      const cityCounts = {}
      regions.forEach(region => {
        const city = region.split(':')[1]
        if (city) {
          cityCounts[city] = (cityCounts[city] || 0) + 1
        }
      })

      return regions
        .map(region => {
          const city = region.split(':')[1]
          // cities are stored however they were first entered ('Calgary',
          // 'vancouver'), so title case them for a consistent-looking list
          const label = city && cityCounts[city] === 1
            ? city.replace(/\b[a-z]/g, letter => letter.toUpperCase())
            : region
          return {label, text: `${label} (${customerCounts[region]})`, value: region}
        })
        .sort((a, b) => {
          if (a.value === NO_REGION) return 1
          if (b.value === NO_REGION) return -1
          return a.text.localeCompare(b.text)
        })
    },
    selectedRegionLabel() {
      const option = this.regionOptions.find(region => region.value === this.selectedRegion)
      return option ? option.label : this.selectedRegion
    },
    dateRangeButtonText() {
      if (!this.startingWeeks && !this.rangeDuration) {
        return 'Set Date Range'
      }
      if (this.startingWeeks && this.rangeDuration) {
        return `${this.startingWeeks}w - ${this.startingWeeks + this.rangeDuration}w`
      }
      if (this.startingWeeks) {
        return `${this.startingWeeks}w back`
      }
      return 'Set Date Range'
    },
    filteredCustomers() {
      // Calculate the date range
      const to = this.startingWeeks !== null
        ? moment().subtract(this.startingWeeks, 'weeks')
        : moment().subtract(208, 'weeks') // Default to very far back if no start specified


      const from = this.startingWeeks !== null && this.rangeDuration !== null
        ? to.clone().subtract(this.rangeDuration, 'weeks')
        : moment() // Default to today if no end specified

      return this.customers.filter(customer => {
        // Filter by stream if selected
        if (this.selectedStream) {
          if (!customer.plan || !this.plans[customer.plan]) {
            return false
          }
          const plan = this.plans[customer.plan]
          if (plan.stream !== this.selectedStream) {
            return false
          }
        }

        // Filter by delivery region (from the customer's postcode) if selected
        if (this.selectedRegion && customer._region !== this.selectedRegion) {
          return false
        }

        // Filter by date range if set
        if (this.startingWeeks !== null || this.rangeDuration !== null) {
          if (!customer._signup) {
            return false
          }
          let signupDate = moment(customer._signup);
          if (!signupDate.isValid()) {
            return false
          }
          signupDate = moment(this.getWeekStartForSignup(signupDate));


          // Check if signup date is within the range
          if (!signupDate.isBetween(from, to, '[]')) {
            // console.log(`NO:  signup date ${signupDate.format(moment.HTML5_FMT.DATE)} not between ${to.format(moment.HTML5_FMT.DATE)}, ${from.format(moment.HTML5_FMT.DATE)}`);
            return false
          } else {
            // console.log(`YES: signup date ${signupDate.format(moment.HTML5_FMT.DATE)} is between ${to.format(moment.HTML5_FMT.DATE)}, ${from.format(moment.HTML5_FMT.DATE)}`);
          }
        }

        return true
      })
    },
    weeklySignups() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      const signupsByWeek = {}
      const churnCutoffDate = moment().subtract(this.churnWeeks, 'weeks');


      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)

          // Filter out invalid dates
          if (!signupDate.isValid()) {
            return
          }

          const weekStart = this.getWeekStartForSignup(signupDate)

          // Filter out invalid week start dates
          if (weekStart === 'Invalid date') {
            return
          }

          if (!signupsByWeek[weekStart]) {
            signupsByWeek[weekStart] = {
              weekStart,
              signupCount: 0,
              utmCampaignCount: 0,
              totalSales: 0,
              totalInvoices: 0,
              purchasedCount: 0,
              churnedCustomers: 0,
            }
          }
          signupsByWeek[weekStart].signupCount++

          // Count customers with UTM campaign data
          if (customer.mixpanel && customer.mixpanel.initial_utm_campaign) {
            signupsByWeek[weekStart].utmCampaignCount++
          }

          // Add sales statistics if available
          if (customer.stats) {
            if (typeof customer.stats.net === 'number') {
              signupsByWeek[weekStart].totalSales += customer.stats.net
            }
            if (typeof customer.stats.invoiceCount === 'number') {
                signupsByWeek[weekStart].totalInvoices += customer.stats.invoiceCount
              if (customer.stats.invoiceCount > 0) {
                signupsByWeek[weekStart].purchasedCount++;
              }
            }
          }
          // Check for churn based on deleted or lastCharge date
          if (customer.isDeleted) {
            signupsByWeek[weekStart].churnedCustomers++
          } else if (customer.stats && customer.stats.lastCharge) {
            const lastChargeDate = moment(customer.stats.lastCharge)
            if (lastChargeDate.isValid() && lastChargeDate.isBefore(churnCutoffDate)) {
              signupsByWeek[weekStart].churnedCustomers++
            }
          }
        }
      })

      const weeklyData = Object.values(signupsByWeek)
        .filter(week => {
          // Filter out any remaining invalid data
          const weekMoment = moment(week.weekStart)
          return weekMoment.isValid() &&
            !isNaN(week.signupCount) &&
            !isNaN(week.utmCampaignCount) &&
            !isNaN(week.totalSales) &&
            !isNaN(week.totalInvoices)
        })
        .sort((a, b) =>
          moment(b.weekStart).valueOf() - moment(a.weekStart).valueOf()
        )

      // Calculate moving average comparison for each week
      return weeklyData.map((week) => {
        const weekMoment = moment(week.weekStart)

        // Get weeks from 2 months ago (8 weeks) up to this week for moving average
        const twoMonthsAgo = weekMoment.clone().subtract(8, 'weeks')

        const relevantWeeks = weeklyData.filter(w => {
          const wMoment = moment(w.weekStart)
          return wMoment.isBefore(weekMoment) && wMoment.isAfter(twoMonthsAgo)
        })

        let movingAverageComparison = null
        let comparisonPercentage = null

        if (relevantWeeks.length > 0) {
          const movingAverage = relevantWeeks.reduce((sum, w) => sum + w.signupCount, 0) / relevantWeeks.length

          if (movingAverage > 0) {
            comparisonPercentage = Math.round(((week.signupCount - movingAverage) / movingAverage) * 100)
            movingAverageComparison = {
              percentage: comparisonPercentage,
              isPositive: comparisonPercentage > 0,
              movingAverage: Math.round(movingAverage * 10) / 10
            }
          }
        }

        // Calculate UTM percentage
        const utmPercentage = week.signupCount > 0
          ? Math.round((week.utmCampaignCount / week.signupCount) * 100)
          : 0

        return {
          ...week,
          vsMovingAverage: movingAverageComparison,
          comparisonPercentage,
          utmPercentage
        }
      })
    },
    weeklySalesData() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      const salesByWeek = {}
      const churnCutoffDate = moment().subtract(this.churnWeeks, 'weeks')

      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)

          if (!signupDate.isValid()) {
            return
          }

          const weekStart = this.getWeekStartForSignup(signupDate)

          if (weekStart === 'Invalid date') {
            return
          }

          if (!salesByWeek[weekStart]) {
            salesByWeek[weekStart] = {
              weekStart,
              totalSales: 0,
              totalInvoices: 0,
              churnedCustomers: 0,
              totalCustomers: 0,
              avgSalesPerInvoice: 0,
              avgNetPerCustomer: 0,
              customerSalesValues: [],
              customerInvoiceValues: [],
              customerSalesPerInvoiceValues: [],
              customerNetPerCustomerValues: []
            }
          }

          // Count total customers in this week
          salesByWeek[weekStart].totalCustomers++

          // Add sales statistics if available
          if (customer.stats) {
            if (typeof customer.stats.net === 'number') {
              salesByWeek[weekStart].totalSales += customer.stats.net
              salesByWeek[weekStart].customerSalesValues.push(customer.stats.net)
              salesByWeek[weekStart].customerNetPerCustomerValues.push(customer.stats.net)
            }
            if (typeof customer.stats.invoiceCount === 'number') {
              salesByWeek[weekStart].totalInvoices += customer.stats.invoiceCount
              salesByWeek[weekStart].customerInvoiceValues.push(customer.stats.invoiceCount)

              // Calculate sales per invoice for this customer if both values are available
              if (typeof customer.stats.net === 'number' && customer.stats.invoiceCount > 0) {
                const customerSalesPerInvoice = customer.stats.net / customer.stats.invoiceCount
                salesByWeek[weekStart].customerSalesPerInvoiceValues.push(customerSalesPerInvoice)
              }
            }

            // Check for churn based on deleted or lastCharge date
            if (customer.isDeleted) {
              salesByWeek[weekStart].churnedCustomers++
            } else if (customer.stats.lastCharge) {
              const lastChargeDate = moment(customer.stats.lastCharge)
              if (lastChargeDate.isValid() && lastChargeDate.isBefore(churnCutoffDate)) {
                salesByWeek[weekStart].churnedCustomers++
              }
            }
          }
        }
      })

      return Object.values(salesByWeek)
        .filter(week => {
          const weekMoment = moment(week.weekStart)
          return weekMoment.isValid() &&
            !isNaN(week.totalSales) &&
            !isNaN(week.totalInvoices) &&
            !isNaN(week.churnedCustomers) &&
            !isNaN(week.totalCustomers)
        })
        .map(week => {
          // Calculate average sales per invoice
          const avgSalesPerInvoice = week.totalInvoices > 0
            ? week.totalSales / week.totalInvoices
            : 0

          // Calculate average net per customer
          const avgNetPerCustomer = week.totalCustomers > 0
            ? week.totalSales / week.totalCustomers
            : 0

          // Calculate churn percentage
          const churnPercentage = week.totalCustomers > 0
            ? Math.round((week.churnedCustomers / week.totalCustomers) * 100)
            : 0

          // Create churn info object with count, percentage, and total customers
          const churnInfo = {
            count: week.churnedCustomers,
            percentage: churnPercentage,
            totalCustomers: week.totalCustomers
          }

          // Calculate median values
          const medianSales = this.calculateMedian(week.customerSalesValues)
          const medianInvoices = this.calculateMedian(week.customerInvoiceValues)
          const medianSalesPerInvoice = this.calculateMedian(week.customerSalesPerInvoiceValues)
          const medianNetPerCustomer = this.calculateMedian(week.customerNetPerCustomerValues)

          return {
            ...week,
            avgSalesPerInvoice,
            avgNetPerCustomer,
            churnInfo,
            medianSales,
            medianInvoices,
            medianSalesPerInvoice,
            medianNetPerCustomer
          }
        })
        .sort((a, b) =>
          moment(b.weekStart).valueOf() - moment(a.weekStart).valueOf()
        )
    },
    totalSalesAmount() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      return this.weeklySalesData.reduce((sum, week) => sum + week.totalSales, 0)
    },
    totalInvoicesAmount() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      return this.weeklySalesData.reduce((sum, week) => sum + week.totalInvoices, 0)
    },
    avgSalesPerInvoiceTotal() {
      return this.totalInvoicesAmount > 0
        ? this.totalSalesAmount / this.totalInvoicesAmount
        : 0
    },
    avgSalesPerCustomer() {
      return this.totalSalesAmount / this.totalCustomers;
    },
    totalChurnedCustomers() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      return this.weeklySalesData.reduce((sum, week) => sum + week.churnedCustomers, 0)
    },
    medianSalesTotal() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      const totalSalesValues = this.weeklySalesData.map(week => week.totalSales)
      return this.calculateMedian(totalSalesValues)
    },
    medianInvoicesTotal() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      const totalInvoiceValues = this.weeklySalesData.map(week => week.totalInvoices)
      return this.calculateMedian(totalInvoiceValues)
    },
    medianSalesPerInvoiceTotal() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      const avgSalesPerInvoiceValues = this.weeklySalesData.map(week => week.avgSalesPerInvoice)
      return this.calculateMedian(avgSalesPerInvoiceValues)
    },
    medianNetPerCustomerTotal() {
      if (!this.weeklySalesData || this.weeklySalesData.length === 0) return 0
      const avgNetPerCustomerValues = this.weeklySalesData.map(week => week.avgNetPerCustomer)
      return this.calculateMedian(avgNetPerCustomerValues)
    },
    totalCustomers() {
      return this.filteredCustomers.filter(c => {
        if (!c._signup) {
          return false
        }
        return moment(c._signup).isValid()
      }).length
    },
    avgSignupsPerWeek() {
      if (this.weeklySignups.length === 0) return 0
      const total = this.weeklySignups.reduce((sum, week) => sum + week.signupCount, 0)
      return Math.round((total / this.weeklySignups.length) * 10) / 10
    },
    avgPurchasedPerWeek() {
      if (this.weeklySignups.length === 0) return 0
      const total = this.weeklySignups.reduce((sum, week) => sum + week.purchasedCount, 0)
      return Math.round((total / this.weeklySignups.length) * 10) / 10
    },
    avgChurnedPerWeek() {
      if (this.weeklySignups.length === 0) return 0
      const total = this.weeklySignups.reduce((sum, week) => sum + week.churnedCustomers, 0)
      return Math.round((total / this.weeklySignups.length) * 10) / 10
    },
    peakWeek() {
      if (this.weeklySignups.length === 0) return 'N/A'
      const peak = this.weeklySignups.reduce((max, week) =>
        week.signupCount > max.signupCount ? week : max
      )
      return `${this.formatWeekRange(peak.weekStart)} (${peak.signupCount} signups)`
    },
    availableYears() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      const years = new Set()
      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            years.add(signupDate.year())
          }
        }
      })

      return Array.from(years).sort((a, b) => b - a) // Most recent first
    },
    yearlyHeaders() {
      const baseHeaders = [
        {text: 'Week', value: 'weekRange', width: '120px'},
      ]

      // Add a column for each year
      const yearHeaders = this.availableYears.map(year => ({
        text: year.toString(),
        value: `year_${year}`,
        width: '100px',
        align: 'center'
      }))

      return [...baseHeaders, ...yearHeaders]
    },
    yearlyComparison() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      // Group signups by year and week number
      const signupsByYearWeek = {}

      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)

          // Filter out invalid dates
          if (!signupDate.isValid()) {
            return
          }

          const year = signupDate.year()
          const weekNumber = signupDate.week()

          // Filter out invalid years or week numbers
          if (isNaN(year) || isNaN(weekNumber)) {
            return
          }

          if (!signupsByYearWeek[weekNumber]) {
            signupsByYearWeek[weekNumber] = {}
          }
          if (!signupsByYearWeek[weekNumber][year]) {
            signupsByYearWeek[weekNumber][year] = 0
          }
          signupsByYearWeek[weekNumber][year]++
        }
      })

      // Convert to table format
      const weeks = Object.keys(signupsByYearWeek).map(Number).sort((a, b) => a - b)
      const sortedYears = [...this.availableYears].sort((a, b) => a - b) // Chronological for comparison

      return weeks.map(weekNumber => {
        const item = {
          weekNumber,
          weekRange: this.getWeekRange(weekNumber)
        }

        // Add data for each year
        sortedYears.forEach((year, index) => {
          const currentYearCount = signupsByYearWeek[weekNumber][year] || 0
          const previousYear = sortedYears[index - 1]
          const previousYearCount = previousYear ? (signupsByYearWeek[weekNumber][previousYear] || 0) : null

          item[`year_${year}`] = currentYearCount

          // Calculate change from previous year
          if (previousYearCount !== null) {
            item[`change_${year}`] = currentYearCount - previousYearCount
          } else {
            item[`change_${year}`] = null
          }
        })

        return item
      })
    },
    yearlySummary() {
      if (!this.availableYears.length) return []

      const sortedYears = [...this.availableYears].sort((a, b) => b - a) // Most recent first

      return sortedYears.map((year, index) => {
        const yearCustomers = this.filteredCustomers.filter(customer => {
          if (!customer._signup) {
            return false
          }
          const signupDate = moment(customer._signup)
          return signupDate.isValid() && signupDate.year() === year
        })

        const totalSignups = yearCustomers.length

        // Calculate weeks in year that have data
        const weeksWithData = new Set()
        yearCustomers.forEach(customer => {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            const weekNumber = signupDate.week()
            if (!isNaN(weekNumber)) {
              weeksWithData.add(weekNumber)
            }
          }
        })

        const avgPerWeek = weeksWithData.size > 0
          ? Math.round((totalSignups / weeksWithData.size) * 10) / 10
          : 0

        // Find peak week for this year
        const weekCounts = {}
        yearCustomers.forEach(customer => {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            const week = signupDate.week()
            if (!isNaN(week)) {
              weekCounts[week] = (weekCounts[week] || 0) + 1
            }
          }
        })

        const peakWeekData = Object.entries(weekCounts).reduce((max, [week, count]) =>
            count > max.count ? {week: Number(week), count} : max
          , {week: null, count: 0})

        const peakWeek = peakWeekData.week
          ? `Week ${peakWeekData.week} (${peakWeekData.count})`
          : 'N/A'

        // Calculate vs previous year
        const nextYear = sortedYears[index + 1]
        let vsLastYear = null

        if (nextYear) {
          const previousYearTotal = this.filteredCustomers.filter(customer => {
            if (!customer._signup) {
              return false
            }
            const signupDate = moment(customer._signup)
            return signupDate.isValid() && signupDate.year() === nextYear
          }).length

          if (previousYearTotal > 0) {
            vsLastYear = Math.round(((totalSignups - previousYearTotal) / previousYearTotal) * 100)
          }
        }

        return {
          year,
          totalSignups,
          avgPerWeek,
          peakWeek,
          vsLastYear
        }
      })
    },
    availableUTMCampaigns() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      const campaigns = new Set()
      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            if (customer.mixpanel && customer.mixpanel.initial_utm_campaign) {
              campaigns.add(customer.mixpanel.initial_utm_campaign)
            } else {
              campaigns.add('(no campaign)')
            }
          }
        }
      })

      // Sort campaigns with '(no campaign)' last
      const sortedCampaigns = Array.from(campaigns).sort((a, b) => {
        if (a === '(no campaign)') return 1
        if (b === '(no campaign)') return -1
        return a.localeCompare(b)
      })

      return sortedCampaigns
    },
    utmHeaders() {
      const baseHeaders = [
        {text: 'Week Starting', value: 'weekStart', width: '200px'}
      ]

      // Add a column for each UTM campaign
      const campaignHeaders = this.availableUTMCampaigns.map(campaign => ({
        text: campaign.length > 15 ? campaign.substring(0, 15) + '...' : campaign,
        value: `utm_${campaign}`,
        width: '120px',
        align: 'center'
      }))

      return [...baseHeaders, ...campaignHeaders]
    },
    utmComparison() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      // Group signups by week and UTM campaign
      const signupsByWeekCampaign = {}

      this.filteredCustomers.forEach(customer => {
        if (customer._signup) {
          const signupDate = moment(customer._signup)

          // Filter out invalid dates
          if (!signupDate.isValid()) {
            return
          }

          const weekStart = this.getWeekStartForSignup(signupDate)

          // Filter out invalid week start dates
          if (weekStart === 'Invalid date') {
            return
          }

          const campaign = (customer.mixpanel && customer.mixpanel.initial_utm_campaign)
            ? customer.mixpanel.initial_utm_campaign
            : '(no campaign)'

          if (!signupsByWeekCampaign[weekStart]) {
            signupsByWeekCampaign[weekStart] = {}
          }
          if (!signupsByWeekCampaign[weekStart][campaign]) {
            signupsByWeekCampaign[weekStart][campaign] = 0
          }
          signupsByWeekCampaign[weekStart][campaign]++
        }
      })

      // Convert to table format
      const weeks = Object.keys(signupsByWeekCampaign).sort((a, b) =>
        moment(b).valueOf() - moment(a).valueOf()
      )

      return weeks.map(weekStart => {
        const item = {
          weekStart
        }

        // Add data for each campaign
        this.availableUTMCampaigns.forEach(campaign => {
          item[`utm_${campaign}`] = signupsByWeekCampaign[weekStart][campaign] || 0
        })

        return item
      }).filter(week => {
        // Filter out any remaining invalid data
        const weekMoment = moment(week.weekStart)
        return weekMoment.isValid()
      })
    },
    // One table per calendar month (of the signup week's start date), most
    // recent first, showing only the campaigns with signups in that month -
    // old campaigns stop occupying a column forever. Zero cells are null so
    // they render blank. utmComparison above stays as the flat all-campaign
    // view used by the CSV export.
    utmMonthlyTables() {
      if (!this.filteredCustomers || this.filteredCustomers.length === 0) return []

      const signupsByWeekCampaign = {}
      this.filteredCustomers.forEach(customer => {
        if (!customer._signup) return
        const signupDate = moment(customer._signup)
        if (!signupDate.isValid()) return
        const weekStart = this.getWeekStartForSignup(signupDate)
        if (weekStart === 'Invalid date') return

        const campaign = (customer.mixpanel && customer.mixpanel.initial_utm_campaign)
          ? customer.mixpanel.initial_utm_campaign
          : '(no campaign)'
        if (!signupsByWeekCampaign[weekStart]) {
          signupsByWeekCampaign[weekStart] = {}
        }
        signupsByWeekCampaign[weekStart][campaign] = (signupsByWeekCampaign[weekStart][campaign] || 0) + 1
      })

      const months = {}
      Object.entries(signupsByWeekCampaign).forEach(([weekStart, campaignCounts]) => {
        const monthKey = weekStart.slice(0, 7) // YYYY-MM
        if (!months[monthKey]) {
          months[monthKey] = {monthKey, weeks: {}, campaigns: new Set()}
        }
        months[monthKey].weeks[weekStart] = campaignCounts
        Object.keys(campaignCounts).forEach(campaign => months[monthKey].campaigns.add(campaign))
      })

      return Object.values(months)
        .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
        .map(month => {
          const campaigns = Array.from(month.campaigns).sort((a, b) => {
            if (a === '(no campaign)') return 1
            if (b === '(no campaign)') return -1
            return a.localeCompare(b)
          })

          const monthTotals = {utmTotal: 0}
          campaigns.forEach(campaign => {
            monthTotals[campaign] = 0
          })
          const items = Object.entries(month.weeks)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([weekStart, campaignCounts]) => {
              const item = {weekStart, utmTotal: 0}
              campaigns.forEach(campaign => {
                const count = campaignCounts[campaign] || 0
                item[`utm_${campaign}`] = count > 0 ? count : null
                monthTotals[campaign] += count
                if (campaign !== '(no campaign)') {
                  item.utmTotal += count
                }
              })
              monthTotals.utmTotal += item.utmTotal
              return item
            })

          const campaignHeader = campaign => {
            const label = campaign.length > 15 ? campaign.substring(0, 15) + '...' : campaign
            return {
              text: `${label} (${monthTotals[campaign]})`,
              value: `utm_${campaign}`,
              width: '120px',
              align: 'center'
            }
          }
          // real campaigns, then the attributed total, with (no campaign) last
          const realCampaigns = campaigns.filter(campaign => campaign !== '(no campaign)')
          const headers = [
            {text: 'Week Starting', value: 'weekStart', width: '200px'},
            ...realCampaigns.map(campaignHeader),
            {text: `UTM Total (${monthTotals.utmTotal})`, value: 'utmTotal', width: '100px', align: 'center'},
            ...campaigns.filter(campaign => campaign === '(no campaign)').map(campaignHeader)
          ]

          return {
            monthKey: month.monthKey,
            title: moment(month.monthKey + '-01').format('MMMM YYYY'),
            headers,
            items
          }
        })
    },
    utmSummary() {
      if (!this.availableUTMCampaigns.length) return []

      const totalCustomersWithUTM = this.filteredCustomers.filter(customer => {
        if (!customer._signup) {
          return false
        }
        return moment(customer._signup).isValid()
      }).length

      return this.availableUTMCampaigns.map(campaign => {
        const campaignCustomers = this.filteredCustomers.filter(customer => {
          if (!customer._signup) {
            return false
          }
          const signupDate = moment(customer._signup)
          if (!signupDate.isValid()) {
            return false
          }

          const customerCampaign = (customer.mixpanel && customer.mixpanel.initial_utm_campaign)
            ? customer.mixpanel.initial_utm_campaign
            : '(no campaign)'

          return customerCampaign === campaign
        })

        const totalSignups = campaignCustomers.length

        // Calculate weeks with data for this campaign
        const weeksWithData = new Set()
        campaignCustomers.forEach(customer => {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            const weekStart = this.getWeekStartForSignup(signupDate)
            if (weekStart !== 'Invalid date') {
              weeksWithData.add(weekStart)
            }
          }
        })

        const avgPerWeek = weeksWithData.size > 0
          ? Math.round((totalSignups / weeksWithData.size) * 10) / 10
          : 0

        // Find peak week for this campaign
        const weekCounts = {}
        campaignCustomers.forEach(customer => {
          const signupDate = moment(customer._signup)
          if (signupDate.isValid()) {
            const weekStart = this.getWeekStartForSignup(signupDate)
            if (weekStart !== 'Invalid date') {
              weekCounts[weekStart] = (weekCounts[weekStart] || 0) + 1
            }
          }
        })

        const peakWeekData = Object.entries(weekCounts).reduce((max, [week, count]) =>
            count > max.count ? {week, count} : max
          , {week: null, count: 0})

        const peakWeek = peakWeekData.week
          ? `${this.formatWeekRange(peakWeekData.week)} (${peakWeekData.count})`
          : 'N/A'

        const percentage = totalCustomersWithUTM > 0
          ? Math.round((totalSignups / totalCustomersWithUTM) * 100)
          : 0

        return {
          campaign,
          totalSignups,
          avgPerWeek,
          peakWeek,
          percentage
        }
      }).sort((a, b) => b.totalSignups - a.totalSignups) // Sort by total signups descending
    }
  },
  methods: {
    getWeekStartForSignup(signupDate) {
      // Find the most recent cutoff (weekCutoffDay at weekCutoffHour) on or before signupDate
      let cutoff = signupDate.clone().day(this.weekCutoffDay).startOf('day').add(this.weekCutoffHour, 'hours')
      if (cutoff.isAfter(signupDate)) {
        cutoff.subtract(1, 'week')
      }
      return cutoff.format('YYYY-MM-DD')
    },
    calculateMedian(values) {
      if (!values || values.length === 0) return 0
      const sorted = [...values].filter(v => typeof v === 'number' && !isNaN(v)).sort((a, b) => a - b)
      if (sorted.length === 0) return 0
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
    },
    formatCurrency,
    formatWeekRange(weekStart) {
      const start = moment(weekStart)
      const end = moment(weekStart).add(6, 'days')
      return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
    },
    ...mapActions([
      'fetchPlans'
    ]),
    // Subaccounts (mainAccount set) don't pay for their own meals - Stripe has
    // nothing for them, so they must not appear in signup or sales numbers.
    excludeSubaccounts(customers) {
      return customers.filter(customer => !customer.mainAccount)
    },
    // Flatten the region -> postcode maps from GET v2/postcodes into a single
    // postcode -> region ('local:Calgary:AB') lookup so each customer costs one
    // map hit per postcode prefix instead of a scan of every region.
    buildPostcodeLookup() {
      const lookup = {}
      Object.entries(this.postcodeRegions).forEach(([region, codes]) => {
        Object.keys(codes || {}).forEach(code => {
          if (codes[code] && !lookup[code]) {
            lookup[code] = region
          }
        })
      })
      return lookup
    },
    // Regions are stored as postcode prefixes (usually the 3-character FSA), so
    // match the way fed-lib's checkPostcode does: the whole postcode first, then
    // progressively longer prefixes.
    regionForPostcode(postalCode, lookup) {
      if (!postalCode) return null
      const code = String(postalCode).toUpperCase().replace(/\s+/g, '')
      if (lookup[code]) return lookup[code]
      for (let i = 1; i < code.length; i++) {
        const region = lookup[code.substring(0, i)]
        if (region) return region
      }
      return null
    },
    assignRegions(customers) {
      const lookup = this.buildPostcodeLookup()
      customers.forEach(customer => {
        const postalCode = customer.address && customer.address.postal_code
        customer._region = this.regionForPostcode(postalCode, lookup) || NO_REGION
      })
      return customers
    },
    // account_created is written at registration; stripe.created_at is only
    // backfilled by the weekly Stripe job - without this fallback order,
    // brand-new customers stay invisible in signup counts until Saturday.
    normalizeSignupDates(customers) {
      customers.forEach(customer => {
        customer._signup = customer.account_created
          || (customer.stripe && customer.stripe.created_at)
          || null
      })
      return customers
    },
    async fetchCustomers(forceRefresh = false) {
      this.loading = true
      this.error = null

      // v3: address (for the region filter) added to the projection - v2 caches
      // predate it, as v1 caches predate the mainAccount field
      const CACHE_KEY = 'customer-analytics-cache-v3'

      if (!forceRefresh) {
        try {
          const cached = JSON.parse(localStorage.getItem(CACHE_KEY))
          if (cached && cached.customers && cached.plans) {
            this.plans = cached.plans
            this.postcodeRegions = cached.postcodeRegions || {}
            this.customers = this.assignRegions(this.normalizeSignupDates(this.excludeSubaccounts(cached.customers)))
            this.cachedAt = cached.cachedAt
            this.loading = false
            return
          }
        } catch (e) {
          console.warn('Failed to read customer analytics cache', e)
        }
      }

      try {
        const [customersResponse, plansResponse, postcodesResponse] = await Promise.all([
          api.get('v2/customers', {
            params: {
              skipShopify: true,
              orderMetrics: false,
              hasMeals: false,
              fields: ANALYTICS_CUSTOMER_FIELDS.join(',')
            }
          }),
          this.fetchPlans(),
          // the region filter is a nicety - a postcode outage shouldn't take the
          // whole page down with it
          api.get('v2/postcodes').catch(e => {
            console.warn('Failed to load postcode regions - region filter unavailable', e)
            return {data: {}}
          })
        ])

        this.plans = plansResponse || {}
        this.postcodeRegions = (postcodesResponse.data.postcodes && postcodesResponse.data.postcodes.regions) || {}
        this.customers = this.assignRegions(this.normalizeSignupDates(this.excludeSubaccounts(customersResponse.data)))
        this.cachedAt = new Date().toISOString()
        console.log('Loaded customers for analytics:', this.customers.length)
        console.log('Loaded plans:', Object.keys(this.plans).length)
        console.log('Loaded postcode regions:', Object.keys(this.postcodeRegions).length)

        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            customers: this.customers,
            plans: this.plans,
            postcodeRegions: this.postcodeRegions,
            cachedAt: this.cachedAt,
          }))
        } catch (e) {
          console.warn('Failed to save customer analytics cache', e)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        this.error = 'Failed to load data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    completeRefresh() {
      const warning = 'Complete refresh re-pulls every customer from Stripe.\n\n'
        + 'This should not normally be needed - the weekly job and the regular Refresh '
        + 'keep the data current - and it is VERY slow (it can take over 30 minutes).\n\n'
        + 'Run it anyway?'
      if (confirm(warning)) {
        this.refreshData('full')
      }
    },
    // Ask the API to re-pull Stripe stats (incremental by default - only
    // customers with new charges/refunds since the last refresh), then
    // refetch. The server runs the refresh in the background, so poll its
    // status until it settles.
    async refreshData(mode = 'incremental') {
      if (this.refreshing) return
      this.refreshing = true
      this.error = null
      this.refreshStatus = null
      this.refreshDialog = true
      try {
        let status = (await api.post('v2/customers/refresh-stats', {mode, mixpanel: true}, {raxConfig: {retry: 0}})).data
        this.refreshStatus = status
        const timeLimit = mode === 'full' ? 60 * 60 * 1000 : 5 * 60 * 1000
        const deadline = Date.now() + timeLimit
        while (status.running && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 2000))
          status = (await api.get('v2/customers/refresh-stats')).data
          this.refreshStatus = status
          this.refreshProgress = status.progress
        }
        if (status.lastError) {
          this.error = 'Stats refresh failed: ' + status.lastError
        } else if (status.lastResult && status.lastResult.mixpanel && status.lastResult.mixpanel.error) {
          this.error = 'Sales stats refreshed, but the UTM (mixpanel) sync failed: '
            + status.lastResult.mixpanel.error
        }
      } catch (error) {
        console.error('Error refreshing stats:', error)
        const serverMessage = error.response && error.response.data && error.response.data.message
        this.error = 'Stats refresh failed: ' + (serverMessage || error.message)
      } finally {
        this.refreshing = false
        this.refreshProgress = null
      }
      await this.fetchCustomers(true)
    },
    getWeekRange(weekNumber) {
      // Use current year as reference for week ranges
      const currentYear = moment().year()
      const startOfWeek = moment().year(currentYear).week(weekNumber).startOf('week')
      const endOfWeek = startOfWeek.clone().endOf('week')

      return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D')}`
    },
    getChangeClass(change) {
      if (change === null || change === 0) return ''
      return change > 0 ? 'success--text' : 'error--text'
    },
    downloadWeeklyCSV() {
      const data = csvAsTable(this.weeklySignups, this.weeklyHeaders)
      const filename = `weekly-customer-signups-${moment().format('YYYY-MM-DD')}.csv`
      csvExport(data, filename)
    },
    downloadYearlyCSV() {
      const data = csvAsTable(this.yearlyComparison, this.yearlyHeaders)
      const filename = `yearly-customer-comparison-${moment().format('YYYY-MM-DD')}.csv`
      csvExport(data, filename)
    },
    downloadUTMCSV() {
      const data = csvAsTable(this.utmComparison, this.utmHeaders)
      const filename = `utm-campaign-breakdown-${moment().format('YYYY-MM-DD')}.csv`
      csvExport(data, filename)
    },
    getPercentageColor(percentage) {
      if (percentage >= 30) return 'success'
      if (percentage >= 15) return 'warning'
      if (percentage >= 5) return 'info'
      return 'grey'
    },
    getMovingAverageColor(percentage) {
      if (percentage >= 25) return 'success'
      if (percentage >= 10) return 'light-green'
      if (percentage >= 0) return 'info'
      if (percentage >= -10) return 'orange'
      if (percentage >= -25) return 'deep-orange'
      return 'error'
    },
    getMovingAverageTextColor(percentage) {
      // Ensure good contrast for text
      if (percentage >= 10 || percentage <= -10) return 'white'
      return 'black'
    },
    getUTMPercentageColor(percentage) {
      if (percentage >= 80) return 'success'
      if (percentage >= 60) return 'light-green'
      if (percentage >= 40) return 'lime'
      if (percentage >= 20) return 'yellow'
      if (percentage >= 10) return 'orange'
      return 'error'
    },
    getUTMPercentageTextColor(percentage) {
      // Ensure good contrast for text
      if (percentage >= 60 || percentage < 10) return 'white'
      return 'black'
    },
    onStreamFilterChange() {
      // This method can be used for any additional logic when stream filter changes
      // Currently, the reactive computed properties handle the filtering automatically
    },
    getChurnColor(churnCount) {
      if (churnCount === 0) return 'success'
      if (churnCount <= 2) return 'warning'
      if (churnCount <= 5) return 'orange'
      return 'error'
    },
    getChurnTextColor(churnCount) {
      if (churnCount === 0) return 'white'
      if (churnCount <= 2) return 'black'
      return 'white'
    },
    downloadSalesCSV() {
      const data = csvAsTable(this.weeklySalesData, this.salesHeaders)
      const filename = `weekly-sales-analytics-${moment().format('YYYY-MM-DD')}.csv`
      csvExport(data, filename)
    },
    openDateRangeDialog() {
      // Initialize temporary variables with current values
      this.tempStartingWeeks = this.startingWeeks
      this.tempRangeDuration = this.rangeDuration
      this.dateRangeDialog = true
    },
    clearDateRange() {
      this.startingWeeks = null
      this.rangeDuration = null
      this.dateRangeDialog = false
    },
    applyDateRange() {
      this.tempStartingWeeks = this.tempStartingWeeks || 0;
      this.tempRangeDuration = this.tempRangeDuration || 0;
      // Basic validation
      if (this.tempStartingWeeks !== null && (this.tempStartingWeeks < 0 || this.tempStartingWeeks > 208)) {
        alert('Starting weeks must be between 0 and 208')
        return
      }
      if (this.tempRangeDuration !== null && (this.tempRangeDuration < 1 || this.tempRangeDuration > 208)) {
        alert('Range duration must be between 1 and 208')
        return
      }
      if (this.tempStartingWeeks !== null && this.tempRangeDuration !== null && (this.tempStartingWeeks + this.tempRangeDuration > 208)) {
        alert('Total range cannot exceed 208 weeks')
        return
      }

      // Copy temporary values to actual data properties
      this.startingWeeks = this.tempStartingWeeks
      this.rangeDuration = this.tempRangeDuration

      this.dateRangeDialog = false
    }
  },
  mixins: [urlState],
  async mounted() {
    this.syncToUrl({param: 'activeTab', urlParam: 'tab', initFromRoute: true, parseCallback: v => v && Number(v)});
    this.syncToUrl({param: 'weeklyTab', urlParam: 'sub_tab', initFromRoute: true, parseCallback: v => v && Number(v)});
    this.syncToUrl({param: 'selectedRegion', urlParam: 'region', initFromRoute: true});
    this.syncToUrl({
      param: 'churnWeeks',
      urlParam: 'churn_range',
      initFromRoute: true,
      parseCallback: v => v && Number(v)
    });
    this.syncToUrl({
      param: 'startingWeeks',
      urlParam: 'starting_weeks',
      initFromRoute: true,
      parseCallback: v => v && Number(v)
    });
    this.syncToUrl({
      param: 'rangeDuration',
      urlParam: 'range_duration',
      initFromRoute: true,
      parseCallback: v => v && Number(v)
    });
    await this.fetchCustomers()
  }
}
</script>

<style scoped>
/* wrapped and unwrapped column headers align at the top instead of dangling
   from the bottom of the tallest header */
::v-deep .v-data-table-header th {
  vertical-align: top;
  padding-top: 8px;
}
</style>